// web/src/routes/Player.tsx
// Task 10 / Task C: the /player screen. Reads the produced GenerationResult off the
// create-flow store (empty store → redirect to /create), mounts RoastPlayer for
// playback (same prop mapping as StudioCreate's preview — the isPaged branch for
// deck-shaped props, doodleImageUrl for photo). Action buttons: Home, Save (persist
// once, stay on the player), Share link (ensure saved → copy /r/:id), and a mode-flip
// that re-runs the opposite mode on the same source. Also drives RoastPlayer's video
// export (Export Video → Download Video, with a Cancel overlay), mirroring StudioCreate.
import { Navigate, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { createFlow } from "@/lib/createFlow";
import { loadResultCache, loadRequestCache } from "@/lib/resultCache";
import type { GenerationResult } from "@/lib/runGeneration";
import { base64ToObjectUrl } from "@/lib/blobUrl";
import { saveGeneratedRoast } from "@/lib/saveGeneratedRoast";
import { RoastPlayer } from "@/player/RoastPlayer";
import type { RoastPlayerHandle } from "@/player/types";
import { canShareVideo, deliverVideo } from "../lib/deliverVideo";
import { recordExport, recordExportCancelled } from "@/lib/usage";
import { requestAppFullscreen, exitAppFullscreen, nativeFullscreenElement } from "@/lib/fullscreen";
import { PERSISTENCE_ENABLED } from "@/config/features";
import { isPaged } from "@/types/roast";
import type { Mode } from "@/types/roast";
import { mintRoastId } from "@/lib/ids";
import { DEFAULT_VOICE } from "@/config/voices";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useImpactEntrance } from "@/hooks/useImpactEntrance";
import { Button } from "@/components/ui/button";
import Gleam from "@/components/Gleam";
import { motion } from "motion/react";

export default function Player(): JSX.Element {
  // The in-memory store dies on refresh — before redirecting away, try the device-local
  // cache (resultCache mirrors every setResult; blob URLs are rebuilt on load). Restored
  // results are re-seeded into createFlow so downstream flows behave identically.
  const [result, setLocalResult] = useState<GenerationResult | null>(() => createFlow.getResult());
  const [restoring, setRestoring] = useState<boolean>(() => createFlow.getResult() === null);
  useEffect(() => {
    if (result) return;
    let live = true;
    void Promise.all([loadResultCache(), loadRequestCache()]).then(([restored, restoredReq]) => {
      if (!live) return;
      // Restore the request first so Flip is enabled by the time the player renders.
      if (restoredReq && !createFlow.get()) createFlow.set(restoredReq);
      if (restored) {
        createFlow.setResult(restored);
        setLocalResult(restored);
      }
      setRestoring(false);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  // Impact "landing" when arriving straight from the ignition explosion (else {}).
  const impactProps = useImpactEntrance();

  // Video export: RoastPlayer owns the capture engine; we drive it via the ref and
  // reflect its status. /player reads the result once at mount, so no reset effect —
  // a Flip remounts the screen (see Task note).
  const playerRef = useRef<RoastPlayerHandle>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [exportMode, setExportMode] = useState<"idle" | "exporting">("idle");
  const [captureSupported, setCaptureSupported] = useState(true);
  // The first-play capture keeps recording ~4.5s past the voice end (music fade-out) —
  // this tracks that window so the button reads "Preparing video…" instead of a
  // tappable "Export Video" that would just start a second, redundant recording.
  const [captureInFlight, setCaptureInFlight] = useState(false);
  // Gate the whole export/share cluster on the roast having been played once. Testers landing
  // on a paused player mistook the "Export Video" button for a play button — so hide every
  // export affordance until first play (capture arms on first play, so any capture signal =
  // a play happened).
  const [hasPlayed, setHasPlayed] = useState(false);

  // No-data-retention guard (tester deploy): nothing is persisted server-side, so an
  // in-app leave (Home / Flip) that would OVERWRITE/clear the generation warns first
  // unless the video has been downloaded. (Refresh/tab-close are safe now — the
  // device-local resultCache restores the last generation — so no beforeunload prompt.)
  const [videoDownloaded, setVideoDownloaded] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const guardLeave = (go: () => void): void => {
    if (videoDownloaded) go();
    else setPendingLeave(() => go);
  };

  // Fullscreen: a CSS "theater" shell (fixed viewport overlay) is the universal mechanism
  // (iPhone has no element-fullscreen API); the native API is layered on when the BUTTON
  // (a real gesture) enters. "auto" = entered by rotating to landscape on a touch device —
  // rotating back exits it; an explicit "user" enter persists until toggled/Esc'd.
  const [fsMode, setFsMode] = useState<false | "user" | "auto">(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const toggleFullscreen = (): void => {
    if (fsMode) {
      setFsMode(false);
      void exitAppFullscreen();
    } else {
      setFsMode("user");
      if (shellRef.current) void requestAppFullscreen(shellRef.current); // best-effort native
    }
  };
  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape) and (pointer: coarse)");
    const apply = (matches: boolean): void =>
      setFsMode((f) => (matches ? (f || "auto") : (f === "auto" ? false : f)));
    apply(mq.matches);
    const on = (e: MediaQueryListEvent | { matches: boolean }): void => apply(e.matches);
    mq.addEventListener("change", on as EventListener);
    return () => mq.removeEventListener("change", on as EventListener);
  }, []);
  useEffect(() => {
    // Sync a native exit (Esc / system gesture) back into the shell state.
    const onFsChange = (): void => {
      if (!nativeFullscreenElement()) setFsMode((f) => (f === "user" ? false : f));
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange as EventListener);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange as EventListener);
    };
  }, []);

  // Photo doodle preview URL (deck doodles ride the slide rasters). Derived from
  // the retained saveInputs base64, mirroring StudioCreate's doodleImageUrl.
  // useState+useEffect (not useMemo) so the object URL is revoked on unmount and
  // survives StrictMode's double-invoke without leaking.
  const [doodleImageUrl, setDoodleImageUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!result || result.saveInputs.source !== "photo" || !result.saveInputs.doodleBase64) {
      setDoodleImageUrl(undefined);
      return;
    }
    const url = base64ToObjectUrl(result.saveInputs.doodleBase64, result.saveInputs.doodleMimeType ?? "image/png");
    setDoodleImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [result]);

  // Checking the device cache (one IDB read) — brief black hold, no flash of redirect.
  if (!result && restoring) {
    return <div className="flex min-h-screen w-full items-center justify-center bg-black" />;
  }
  if (!result) return <Navigate to="/create" replace />;

  const { artifact } = result;
  const paged = isPaged(artifact);

  const fail = (e: unknown): void => {
    toast({
      title: "Something went wrong",
      description: e instanceof Error ? e.message : "Please try again.",
      variant: "destructive",
    });
  };

  // Persist once and remember the id. Returns the id so Share can chain off it.
  const persist = async (): Promise<string | null> => {
    if (!user) return null;
    if (savedId) return savedId;
    const id = await saveGeneratedRoast(result, { uid: user.uid, email: user.email });
    setSavedId(id);
    return id;
  };

  const handleSave = async (): Promise<void> => {
    if (!user || saving || savedId) return;
    setSaving(true);
    try {
      await persist();
      toast({ title: "Saved to your library" });
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async (): Promise<void> => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const id = await persist();
      if (!id) return;
      await navigator.clipboard.writeText(`${location.origin}/r/${id}`);
      toast({ title: "Link copied" });
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  const handleFlip = (): void => {
    const req = createFlow.get();
    if (!req) return;
    const opp: Mode = req.mode === "roast" ? "toast" : "roast";
    createFlow.set({ ...req, mode: opp, roastId: mintRoastId(), voiceId: DEFAULT_VOICE[opp] });
    createFlow.setResult(null);
    navigate("/processing", { viewTransition: true });
  };

  const canFlip = createFlow.get() !== null;
  const flipLabel = artifact.mode === "roast" ? "Toast it" : "Roast it";

  return (
    <motion.main className="relative flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-black px-6 py-8" {...impactProps}>
      {/* Display-only rounded corners. The video export is unaffected: RoastPlayer's capture
          compositor draws every layer onto a square export canvas — it never screenshots this
          rounded wrapper — so exported frames stay square. */}
      <div
        ref={shellRef}
        data-testid="player-shell"
        className={fsMode
          ? "fixed inset-0 z-50 overflow-hidden bg-black"
          : "relative w-full overflow-hidden rounded-3xl"}
        style={fsMode ? undefined : {
          aspectRatio: artifact.aspect === "9:16" ? "9 / 16" : "16 / 9",
          maxHeight: "70vh",
          margin: "0 auto",
          ...(artifact.aspect === "9:16" ? { maxWidth: "min(100%, 39.4vh)" } : {}),
        }}
      >
        <RoastPlayer
          ref={playerRef}
          mode={artifact.mode}
          script={artifact.script}
          audioUrl={artifact.audioUrl!}
          imageUrl={paged ? null : artifact.imageUrl}
          wordTimings={artifact.wordTimings}
          detectedObjects={paged ? [] : artifact.detectedObjects}
          isCharacterFullscreen={artifact.isCharacterFullscreen}
          aspect={artifact.aspect}
          doodleImageUrl={paged ? undefined : doodleImageUrl}
          audio={artifact.audio}
          captionMask={artifact.captionMask}
          bleepMode={artifact.bleepMode}
          fullscreen={!!fsMode}
          onToggleFullscreen={toggleFullscreen}
          captureFirstPlay
          onCaptureComplete={(blob) => {
            setVideoBlob(blob);
            setExportMode("idle");
            setHasPlayed(true); // a finished capture means the roast played
          }}
          onCaptureStatusChange={(s) => {
            if (s === "unsupported") { setCaptureSupported(false); setExportMode("idle"); }
            if (s === "aborted") setExportMode("idle");
            if (s === "recording") setCaptureInFlight(true);
            if (s === "complete" || s === "aborted" || s === "unsupported") setCaptureInFlight(false);
            // Capture arms on first play → recording/complete/aborted all imply the user played.
            if (s === "recording" || s === "complete" || s === "aborted") setHasPlayed(true);
          }}
          {...(paged
            ? { keySlides: artifact.keySlides, fillerImages: artifact.fillerImages, slideSchedule: artifact.slideSchedule }
            : {})}
        />
        {exportMode === "exporting" && (
          <div
            data-testid="export-overlay"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/70 text-white"
          >
            <span>Exporting video — keep the screen on and stay on this page</span>
            <Button
              data-testid="export-cancel"
              variant="secondary"
              onClick={() => { playerRef.current?.cancelExport(); setExportMode("idle"); }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button
          variant="ghost"
          onClick={() => guardLeave(() => navigate("/create"))}
          aria-label="Home"
          disabled={exportMode === "exporting"}
          className="text-white/70 hover:bg-white/10 hover:text-white"
        >
          Home
        </Button>
        {/* Persistence UI hidden for the tester deploy (no data retention) — see config/features. */}
        {PERSISTENCE_ENABLED && (
          <>
            <Button variant="secondary" onClick={handleSave} disabled={saving || savedId !== null}>
              {savedId ? "Saved" : saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="secondary" onClick={handleShare} disabled={saving}>
              Share link
            </Button>
          </>
        )}
        {captureSupported && exportMode !== "exporting" && hasPlayed && (
          videoBlob ? (
            <Button
              data-testid="download-video"
              className="relative overflow-hidden bg-red-600 text-white hover:bg-red-500"
              onClick={() => {
                const source = artifact.sourceType ?? "photo";
                const isTouch = canShareVideo();
                void deliverVideo(videoBlob, `${artifact.mode}-${source}-${Date.now()}.mp4`)
                  .then((outcome) => {
                    if (outcome === "cancelled") {
                      // "cancelled" = the user closed the sheet — the file never left the app,
                      // so the download-or-lose-it guard must stay up.
                      recordExportCancelled({ source, isTouch });
                    } else {
                      setVideoDownloaded(true);
                      recordExport({ email: user?.email ?? null, uid: user?.uid ?? null, outcome, source, isTouch });
                    }
                  });
              }}
            >
              {canShareVideo() ? "Share Video" : "Download Video"}
              <Gleam className="rounded-full" opacity={0.45} />
            </Button>
          ) : captureInFlight ? (
            <Button data-testid="preparing-video" variant="secondary" disabled>
              Preparing video…
            </Button>
          ) : (
            <Button
              data-testid="export-video"
              variant="secondary"
              onClick={() => { setExportMode("exporting"); playerRef.current?.exportVideo(); }}
            >
              Export Video
            </Button>
          )
        )}
        <Button onClick={() => guardLeave(handleFlip)} disabled={!canFlip || exportMode === "exporting"}>
          {flipLabel}
        </Button>
      </div>

      {/* Leave-without-download warning: nothing is persisted, so this roast is gone the
          moment they navigate away. Mirrors the export-overlay visual idiom. */}
      {pendingLeave && (
        <div
          data-testid="leave-warning"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-6"
        >
          <div className="w-full max-w-sm rounded-2xl border border-white/15 bg-zinc-900 p-6 text-center">
            <p className="text-lg font-semibold text-white">Download your video first?</p>
            <p className="mt-2 text-sm text-white/60">
              Nothing is saved on our side — if you leave now, this {artifact.mode} is gone for good.
              Export and download the video to keep it.
            </p>
            <div className="mt-5 flex justify-center gap-3">
              <Button variant="secondary" onClick={() => setPendingLeave(null)}>
                Stay
              </Button>
              <Button
                variant="ghost"
                className="text-white/70 hover:bg-white/10 hover:text-white"
                onClick={() => { const go = pendingLeave; setPendingLeave(null); go(); }}
              >
                Leave anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </motion.main>
  );
}
